import type {Repository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitsByAuthor} from '@github-ui/paths'
import {ProfileReference} from '@github-ui/profile-reference'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import type {Author} from '../commit-attribution-types'
import {isBotOrApp, shouldHideHovercard} from '../utils'
import {AuthorTooltip} from './AuthorTooltip'
import {useAuthorSettings} from '../contexts/AuthorSettingsContext'
import {AuthorDisplayName} from './AuthorDisplayName'

import styles from './AuthorAvatar.module.css'

type AuthorAvatarProps = {
  author: Author | undefined
  repo: Pick<Repository, 'ownerLogin' | 'name'>
  className?: string
}

/**
 * Renders the author of a commit.
 */
export function AuthorAvatar({author, repo, className}: AuthorAvatarProps) {
  const authorSettings = useAuthorSettings()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = shouldHideHovercard(author)
    ? {}
    : getHovercardAttributesForType('user', {login: author?.login ?? ''})

  if (!author) return null

  const avatar = (
    <GitHubAvatar
      aria-label={`${author.login || 'author'}`}
      src={author.avatarUrl}
      alt={`${author.login || 'author'}`}
      size={authorSettings.avatarSize}
      square={isBotOrApp(author)}
      className={styles.authorAvatarImage}
    />
  )

  return (
    <div data-testid="author-avatar" className={clsx(styles.AuthorAvatarContainer, className)}>
      {author.path ? (
        <Link href={author.path} data-testid="avatar-icon-link" {...(author.login ? hovercardAttrs : {})}>
          {avatar}
        </Link>
      ) : (
        avatar
      )}
      {author.login ? (
        <AuthorTooltip author={author} renderTooltip={authorSettings.includeTooltip}>
          <Link
            href={commitsByAuthor({repo, author: author.login})}
            aria-label={`commits by ${author.login}`}
            {...hovercardAttrs}
            style={{
              '--author-font-weight': authorSettings.fontWeight,
              '--author-font-color': authorSettings.fontColor,
            }}
            className={styles.authorHoverableLink}
          >
            <ProfileReference login={author.login} profileName={author.profileName} isAgent={false} />
          </Link>
        </AuthorTooltip>
      ) : (
        <AuthorDisplayName displayName={author.displayName} authorSettings={authorSettings} />
      )}
    </div>
  )
}
